(function () {
  const MOBILE_QUERY = '(max-width: 768px)';
  const TAP_MOVE_THRESHOLD = 10;
  const MIN_SCALE = 1;
  const MAX_SCALE = 3;
  const TAP_SCALE = 2;

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  class ProductImageZoom {
    constructor(opener) {
      this.opener = opener;
      this.container = opener.querySelector('.product__media-zoom-container');
      this.image = opener.querySelector('.product__media-zoom-container img');
      this.toggle = opener.querySelector('.product__media-toggle');
      this.zoomHint = opener.querySelector('.product__media-zoom-hint');
      this.slider = opener.closest('slider-component');

      if (!this.container || !this.image) return;

      this.scale = MIN_SCALE;
      this.translateX = 0;
      this.translateY = 0;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.lastTouchX = 0;
      this.lastTouchY = 0;
      this.initialPinchDistance = 0;
      this.initialPinchScale = MIN_SCALE;
      this.isPanning = false;
      this.didPan = false;
      this.isPinching = false;
      this.tapStartX = 0;
      this.tapStartY = 0;
      this.ignoreNextClick = false;
      this.originalSrc = '';
      this.originalSrcset = null;
      this.originalSizes = null;

      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onToggleClick = this.onToggleClick.bind(this);
      this.onZoomHintClick = this.onZoomHintClick.bind(this);
      this.onSlideChanged = this.onSlideChanged.bind(this);

      this.container.addEventListener('touchstart', this.onTouchStart, { passive: false });
      this.container.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.container.addEventListener('touchend', this.onTouchEnd);
      this.container.addEventListener('touchcancel', this.onTouchEnd);

      if (this.toggle) {
        this.toggle.addEventListener('click', this.onToggleClick, true);
      }

      if (this.zoomHint) {
        this.zoomHint.addEventListener('click', this.onZoomHintClick);
      }

      if (this.slider) {
        this.slider.addEventListener('slideChanged', this.onSlideChanged);
      }
    }

    onToggleClick(event) {
      if (!this.isActive()) return;

      if (this.ignoreNextClick) {
        this.ignoreNextClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this.toggleZoom(event.clientX, event.clientY);
    }

    onZoomHintClick(event) {
      if (!this.isActive()) return;

      event.preventDefault();
      event.stopPropagation();

      if (this.scale > MIN_SCALE) {
        this.reset();
        return;
      }

      const rect = this.container.getBoundingClientRect();
      this.toggleZoom(rect.left + rect.width / 2, rect.top + rect.height / 2);
    }

    onSlideChanged(event) {
      const mySlide = this.opener.closest('.slider__slide, .product__media-item');
      if (!mySlide) return;

      const activeSlide = event?.detail?.currentElement;
      if (activeSlide && activeSlide !== mySlide) {
        this.reset();
      }
    }

    isActive() {
      return isMobile() && this.opener.dataset.mobileZoom === 'true';
    }

    shouldBlockSliderSwipe() {
      return this.scale > MIN_SCALE || this.isPinching;
    }

    blockSliderSwipe(event) {
      if (!this.shouldBlockSliderSwipe()) return;
      event.stopPropagation();
    }

    setSliderLocked(locked) {
      if (!this.slider) return;
      this.slider.classList.toggle('media-zoom-active', locked);
    }

    updateZoomHint() {
      if (!this.zoomHint) return;

      const isZoomed = this.scale > MIN_SCALE;
      this.zoomHint.setAttribute('aria-pressed', isZoomed ? 'true' : 'false');
      this.zoomHint.setAttribute(
        'aria-label',
        isZoomed
          ? this.zoomHint.dataset.zoomOutLabel || 'Zoom out'
          : this.zoomHint.dataset.zoomInLabel || 'Zoom in'
      );
    }

    onTouchStart(event) {
      if (!this.isActive()) return;

      if (event.touches.length === 2) {
        this.isPinching = true;
        this.isPanning = false;
        this.didPan = false;
        this.initialPinchDistance = this.getPinchDistance(event.touches);
        this.initialPinchScale = this.scale;
        this.setSliderLocked(true);
        event.stopPropagation();
        if (this.scale <= MIN_SCALE) {
          this.loadHighResImage();
        }
        return;
      }

      this.blockSliderSwipe(event);

      if (event.touches.length !== 1) return;

      const touch = event.touches[0];
      this.tapStartX = touch.clientX;
      this.tapStartY = touch.clientY;
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.lastTouchX = touch.clientX;
      this.lastTouchY = touch.clientY;
      this.isPanning = false;
      this.didPan = false;
      this.isPinching = false;
    }

    onTouchMove(event) {
      if (!this.isActive()) return;

      this.blockSliderSwipe(event);

      if (event.touches.length === 2) {
        event.preventDefault();
        const distance = this.getPinchDistance(event.touches);
        if (!this.initialPinchDistance) {
          this.initialPinchDistance = distance;
          this.initialPinchScale = this.scale;
        }

        const nextScale = this.clampScale(this.initialPinchScale * (distance / this.initialPinchDistance));
        this.setScale(nextScale);
        return;
      }

      if (this.scale <= MIN_SCALE || event.touches.length !== 1) return;

      const touch = event.touches[0];
      const movedX = Math.abs(touch.clientX - this.touchStartX);
      const movedY = Math.abs(touch.clientY - this.touchStartY);

      if (movedX < TAP_MOVE_THRESHOLD && movedY < TAP_MOVE_THRESHOLD) return;

      this.isPanning = true;
      this.didPan = true;
      event.preventDefault();

      const deltaX = touch.clientX - this.lastTouchX;
      const deltaY = touch.clientY - this.lastTouchY;

      this.lastTouchX = touch.clientX;
      this.lastTouchY = touch.clientY;
      this.translateX += deltaX;
      this.translateY += deltaY;
      this.clampTranslate();
      this.applyTransform();
    }

    onTouchEnd(event) {
      if (!this.isActive()) return;

      this.blockSliderSwipe(event);

      if (this.isPinching) {
        this.isPinching = false;
        this.initialPinchDistance = 0;
        if (this.scale <= MIN_SCALE + 0.05) this.reset();
        this.isPanning = false;
        this.didPan = false;
        return;
      }

      if (event.changedTouches.length === 1) {
        const touch = event.changedTouches[0];
        const movedX = Math.abs(touch.clientX - this.tapStartX);
        const movedY = Math.abs(touch.clientY - this.tapStartY);

        if (!this.didPan && movedX < TAP_MOVE_THRESHOLD && movedY < TAP_MOVE_THRESHOLD) {
          event.preventDefault();
          this.ignoreNextClick = true;
          this.toggleZoom(touch.clientX, touch.clientY);
        }
      }

      this.isPanning = false;
      this.didPan = false;
    }

    toggleZoom(clientX, clientY) {
      if (this.scale > MIN_SCALE) {
        this.reset();
        return;
      }

      this.loadHighResImage().then(() => {
        this.zoomToPoint(clientX, clientY, TAP_SCALE);
      });
    }

    loadHighResImage() {
      const zoomSrc = this.image.dataset.zoomSrc;
      if (!zoomSrc) return Promise.resolve();

      if (this.image.dataset.zoomLoaded === 'true') {
        return Promise.resolve();
      }

      this.showImageLoader();

      return new Promise((resolve) => {
        const hiRes = new Image();
        hiRes.onload = () => {
          if (this.originalSrcset === null) {
            this.originalSrc = this.image.getAttribute('src') || '';
            this.originalSrcset = this.image.getAttribute('srcset') || '';
            this.originalSizes = this.image.getAttribute('sizes') || '';
          }

          this.image.removeAttribute('srcset');
          this.image.removeAttribute('sizes');
          this.image.src = zoomSrc;
          this.image.dataset.zoomLoaded = 'true';
          this.hideImageLoader();
          resolve();
        };
        hiRes.onerror = () => {
          this.hideImageLoader();
          resolve();
        };
        hiRes.src = zoomSrc;
      });
    }

    showImageLoader() {
      if (!this.container) return;
      this.container.classList.remove('image-loaded');
    }

    hideImageLoader() {
      if (!this.container) return;
      this.container.classList.add('image-loaded');
    }

    zoomToPoint(clientX, clientY, scale) {
      const rect = this.container.getBoundingClientRect();
      const originX = ((clientX - rect.left) / rect.width) * 100;
      const originY = ((clientY - rect.top) / rect.height) * 100;

      this.container.style.setProperty('--zoom-origin-x', `${originX}%`);
      this.container.style.setProperty('--zoom-origin-y', `${originY}%`);
      this.setScale(this.clampScale(scale));
    }

    setScale(scale) {
      this.scale = this.clampScale(scale);
      const isZoomed = this.scale > MIN_SCALE;
      this.opener.classList.toggle('is-zoomed', isZoomed);
      this.setSliderLocked(isZoomed || this.isPinching);
      this.updateZoomHint();

      if (this.scale <= MIN_SCALE) {
        this.translateX = 0;
        this.translateY = 0;
      } else {
        this.clampTranslate();
      }

      this.applyTransform();
    }

    clampTranslate() {
      if (this.scale <= MIN_SCALE) {
        this.translateX = 0;
        this.translateY = 0;
        return;
      }

      const rect = this.container.getBoundingClientRect();
      const overflowX = rect.width * (this.scale - 1);
      const overflowY = rect.height * (this.scale - 1);

      const originX = parseFloat(this.container.style.getPropertyValue('--zoom-origin-x')) || 50;
      const originY = parseFloat(this.container.style.getPropertyValue('--zoom-origin-y')) || 50;

      const maxX = (originX / 100) * overflowX;
      const minX = -((100 - originX) / 100) * overflowX;
      const maxY = (originY / 100) * overflowY;
      const minY = -((100 - originY) / 100) * overflowY;

      this.translateX = Math.min(maxX, Math.max(minX, this.translateX));
      this.translateY = Math.min(maxY, Math.max(minY, this.translateY));
    }

    applyTransform() {
      const originX = this.container.style.getPropertyValue('--zoom-origin-x') || '50%';
      const originY = this.container.style.getPropertyValue('--zoom-origin-y') || '50%';
      this.image.style.transformOrigin = `${originX} ${originY}`;
      this.image.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
    }

    reset() {
      this.scale = MIN_SCALE;
      this.translateX = 0;
      this.translateY = 0;
      this.initialPinchDistance = 0;
      this.isPanning = false;
      this.didPan = false;
      this.isPinching = false;
      this.opener.classList.remove('is-zoomed');
      this.setSliderLocked(false);
      this.updateZoomHint();
      this.image.style.transform = '';
      this.image.style.transformOrigin = '';
      this.container.style.removeProperty('--zoom-origin-x');
      this.container.style.removeProperty('--zoom-origin-y');

      if (this.originalSrcset !== null) {
        if (this.originalSrcset) {
          this.image.setAttribute('srcset', this.originalSrcset);
        } else {
          this.image.removeAttribute('srcset');
        }

        if (this.originalSizes) {
          this.image.setAttribute('sizes', this.originalSizes);
        } else {
          this.image.removeAttribute('sizes');
        }

        if (this.originalSrc) {
          this.image.src = this.originalSrc;
        }
      }
    }

    clampScale(scale) {
      return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
    }

    getPinchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    }

    destroy() {
      this.container.removeEventListener('touchstart', this.onTouchStart);
      this.container.removeEventListener('touchmove', this.onTouchMove);
      this.container.removeEventListener('touchend', this.onTouchEnd);
      this.container.removeEventListener('touchcancel', this.onTouchEnd);

      if (this.toggle) {
        this.toggle.removeEventListener('click', this.onToggleClick, true);
      }

      if (this.zoomHint) {
        this.zoomHint.removeEventListener('click', this.onZoomHintClick);
      }

      if (this.slider) {
        this.slider.removeEventListener('slideChanged', this.onSlideChanged);
      }

      this.reset();
    }
  }

  const instances = new WeakMap();

  function initProductImageZoom(scope = document) {
    if (typeof window.initImageWithLoaders === 'function') {
      window.initImageWithLoaders(scope);
    }

    scope.querySelectorAll('.product__modal-opener--mobile-zoom').forEach((opener) => {
      if (instances.has(opener)) return;
      const instance = new ProductImageZoom(opener);
      if (instance.image) {
        instances.set(opener, instance);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => initProductImageZoom());
  document.addEventListener('shopify:section:load', (event) => initProductImageZoom(event.target));

  window.initProductImageZoom = initProductImageZoom;
})();
